/* eslint-disable @typescript-eslint/ban-types */
import { Injectable } from '@angular/core';
import { RxDatabase, createRxDatabase, RxCollection } from 'rxdb';
import { columnSchema, referenceTableSchema, tableSchema, WorkspaceDatabaseDbContext } from './schemas';
import { IDatabaseProvider } from '../infrastructure/database-provider.interface';
import { LoggingService } from '@shared/logging';
import { RxdbAdaptorProvider as RxdbAdapterProvider } from '../infrastructure/rxdb-adaptor.provider';
import { ColumnModel, ErrorCodes, Dictionary } from '@shared';

@Injectable()
export class WorkspaceDatabaseProvider implements IDatabaseProvider<WorkspaceDatabaseDbContext> {
	private database: RxDatabase<WorkspaceDatabaseDbContext>;

	private activeDatabases: Dictionary<RxDatabase<WorkspaceDatabaseDbContext>> = {};

	public constructor(
		private logger: LoggingService,
		private adapterProvider: RxdbAdapterProvider
	) {}

	public isActiveDatabase(databaseName: string): boolean {
		if (this.database && this.database.name === databaseName) {
			return true;
		}
		return false;
	}

	public getCurrentDatabaseInstance(): RxDatabase<WorkspaceDatabaseDbContext> {
		if (!this.database) {
			throw new Error('Database not initialized');
		}
		return this.database;
	}

	public async initializeDatabase(
		databaseName: string,
		shouldIgnoreDuplicate = true
	): Promise<RxDatabase<WorkspaceDatabaseDbContext>> {
		if (this.activeDatabases[databaseName]) {
			this.database = this.activeDatabases[databaseName];
			return this.activeDatabases[databaseName];
		}

		const adapter = this.adapterProvider.getAdaptor();

		try {
			this.database = await createRxDatabase({
				name: databaseName,
				storage: adapter,
				ignoreDuplicate: shouldIgnoreDuplicate,
				localDocuments: true
			});
		} catch (error) {
			this.logger.error(ErrorCodes.InitializeDatabase, error);
		}

		await this.database.addCollections({
			tables: {
				schema: tableSchema
			}
		});

		await this.database.addCollections({
			columns: {
				schema: columnSchema,
				migrationStrategies: {
					1: (oldColumn: ColumnModel) => {
						oldColumn.inherit_values_column = '';
						return oldColumn;
					}
				}
			}
		});

		await this.database.addCollections({
			referencetables: {
				schema: referenceTableSchema
			}
		});

		this.activeDatabases[databaseName] = this.database;

		return this.activeDatabases[databaseName];
	}

	public getDatabaseCollection(collectionName: string): RxCollection<any, {}, { [key: string]: any }> {
		if (!this.database) {
			throw new Error(
				'Database is not initialized. Please make sure the database is initialized before getting the collection'
			);
		}
		return this.database[collectionName];
	}
}
